"use client";
import type { InputCityProperties } from ".";
import { InputText } from "@jetblue/kit/input-text";
import {
  autoUpdate,
  offset,
  useDismiss,
  useFloating,
  useInteractions,
  useClick,
  useListNavigation,
  FloatingPortal,
  useFocus,
  flip,
} from "@floating-ui/react";
import { useEffect, useId, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useFilteredItems } from "./use-filtered-items";
import { TextHighlight } from "./text-highlight";
import { useFlightSearchInput } from "./use-flight-search-input";
import { mergeRefs } from "react-merge-refs";

export const InputCityDesktop = ({
  className,
  items = [],
  labels,
  selectedKey,
  setSelectedKey,
  isRequired,
  error,
  showValidation,
}: InputCityProperties) => {
  const elementsReference = useRef<(HTMLElement | null)[]>([]);

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useFlightSearchInput(
    items,
    selectedKey,
    isOpen,
  );
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const getOptionId = (code: string, index: number) =>
    `${baseId}-option-${code}-${index.toString()}`;

  const filteredItems = useFilteredItems(inputValue, items);

  const { refs, floatingStyles, context } = useFloating({
    middleware: [flip(), offset(1)],
    onOpenChange: (open) => {
      setIsOpen(open);
    },
    open: isOpen,
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    enabled: isOpen,
    estimateSize: () => 40,
    getScrollElement: () => refs.floating.current,
    overscan: 5,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [
      useClick(context),
      useFocus(context),
      useDismiss(context),
      useListNavigation(context, {
        activeIndex,
        listRef: elementsReference,
        onNavigate: setActiveIndex,
        virtual: true,
      }),
    ],
  );

  const activeOptionId =
    isOpen && activeIndex !== null && filteredItems[activeIndex]
      ? getOptionId(filteredItems[activeIndex].code, activeIndex)
      : undefined;

  useEffect(() => {
    setActiveIndex(0);
    if (isOpen) {
      virtualizer.scrollToIndex(0);
    }
  }, [filteredItems, isOpen, virtualizer]);

  return (
    <>
      <div className={className}>
        <InputText
          autoComplete="off"
          error={error}
          inputProperties={{
            "aria-activedescendant": activeOptionId,
            "aria-autocomplete": "list",
            "aria-controls": listboxId,
            "aria-expanded": isOpen,
            "aria-haspopup": "listbox",
            ref: refs.setReference,
            role: "combobox",
          }}
          isInvalid={showValidation ? !selectedKey : undefined}
          label={labels.input}
          selectOnFocus
          {...getReferenceProps({
            onKeyDown(event) {
              if (
                event.key === "Enter" &&
                activeIndex != null &&
                filteredItems[activeIndex]
              ) {
                event.preventDefault();
                setSelectedKey(filteredItems[activeIndex].code);
                setActiveIndex(null);
                setIsOpen(false);
              }
            },
          })}
          isRequired={isRequired}
          onChange={(event) => {
            setInputValue(event.target.value);
          }}
          value={inputValue}
        />
      </div>
      <FloatingPortal>
        {isOpen && (
          <div
            className="z-99999 max-h-80 w-96 overflow-auto rounded-b-md border border-medium-gray bg-white"
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <div aria-live="polite" className="sr-only">
              {filteredItems.length === 0
                ? "No results."
                : `${filteredItems.length.toString()} results available.`}
            </div>
            <div
              className="relative"
              style={{
                height: virtualizer.getTotalSize(),
              }}
            >
              <ul
                aria-label={labels.input}
                className="absolute top-0 left-0 w-full"
                id={listboxId}
                role="listbox"
                style={{
                  transform: `translateY(${virtualizer.getVirtualItems()[0]?.start.toString() ?? "0"}px)`,
                }}
              >
                {virtualizer.getVirtualItems().map((vItem) => {
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- safety: they are based on the same list
                  const { indent, code, name } = filteredItems[vItem.index]!;
                  return (
                    <li
                      aria-posinset={vItem.index + 1}
                      aria-selected={selectedKey === code}
                      aria-setsize={filteredItems.length}
                      className="flex w-full cursor-pointer px-4 py-2 text-royal-blue data-current:bg-royal-blue data-current:text-white"
                      data-current={
                        activeIndex === vItem.index ? "" : undefined
                      }
                      data-index={vItem.index}
                      id={getOptionId(code, vItem.index)}
                      key={vItem.key}
                      ref={mergeRefs([
                        virtualizer.measureElement,
                        (node) => {
                          elementsReference.current[vItem.index] = node;
                        },
                      ])}
                      role="option"
                      value={code}
                      {...getItemProps({
                        onClick: () => {
                          setSelectedKey(code);
                          setIsOpen(false);
                        },
                      })}
                    >
                      {indent && (
                        <span
                          aria-hidden
                          className="mr-0.5 before:content-['\2014']"
                        />
                      )}
                      <TextHighlight highlight={inputValue}>
                        {name}
                      </TextHighlight>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  );
};
